import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { performanceApi } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  Target,
  Star,
  Plus,
  TrendingUp,
  Award,
  Calendar,
  CheckCircle2,
  Clock,
  BarChart3,
  Edit3,
  Eye,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const RATING_COLORS = {
  1: 'text-red-500',
  2: 'text-orange-500',
  3: 'text-amber-500',
  4: 'text-green-500',
  5: 'text-emerald-500'
};

const GOAL_STATUS_COLORS = {
  not_started: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700'
};

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-red-100 text-red-700'
};

export default function PerformancePage() {
  const { user, canAccessHR, isEmployee } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [goalForm, setGoalForm] = useState({
    employee_id: '',
    employee_name: '',
    title: '',
    description: '',
    category: 'performance',
    target_date: '',
    priority: 'medium'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reviewsRes, goalsRes] = await Promise.all([
        performanceApi.getReviews({}),
        performanceApi.getGoals({})
      ]);
      setReviews(reviewsRes.data);
      setGoals(goalsRes.data);
    } catch (error) {
      console.error('Failed to fetch performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    try {
      await performanceApi.createGoal(goalForm);
      toast.success('Goal created successfully');
      setShowGoalModal(false);
      resetGoalForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create goal');
    }
  };

  const handleUpdateGoalProgress = async (goalId, progress) => {
    try {
      const status = progress >= 100 ? 'completed' : 'in_progress';
      await performanceApi.updateGoal(goalId, { progress, status });
      toast.success('Progress updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const resetGoalForm = () => {
    setGoalForm({
      employee_id: '',
      employee_name: '',
      title: '',
      description: '',
      category: 'performance',
      target_date: '',
      priority: 'medium'
    });
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
      />
    ));
  };

  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;
  
  const completedGoals = goals.filter(g => g.status === 'completed').length;
  const inProgressGoals = goals.filter(g => g.status === 'in_progress').length;

  return (
    <div className="space-y-6" data-testid="performance-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="page-title">Performance Management</h1>
          <p className="page-description">Track goals, reviews, and employee performance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setShowGoalModal(true)} data-testid="create-goal-btn">
            <Target className="h-4 w-4" />
            Set Goal
          </Button>
          {canAccessHR && (
            <Button className="btn-primary gap-2" onClick={() => setShowReviewModal(true)}>
              <Plus className="h-4 w-4" />
              New Review
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="stat-card border-l-4 border-l-amber-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Rating</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-3xl font-bold">{avgRating}</p>
                  <Star className="h-6 w-6 fill-amber-400 text-amber-400" />
                </div>
              </div>
              <Award className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Reviews</p>
                <p className="text-3xl font-bold text-blue-600">{reviews.length}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Goals Completed</p>
                <p className="text-3xl font-bold text-green-600">{completedGoals}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card border-l-4 border-l-purple-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">In Progress</p>
                <p className="text-3xl font-bold text-purple-600">{inProgressGoals}</p>
              </div>
              <Clock className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="goals" className="space-y-4">
        <TabsList>
          <TabsTrigger value="goals" className="gap-2">
            <Target className="h-4 w-4" />
            Goals
          </TabsTrigger>
          <TabsTrigger value="reviews" className="gap-2">
            <Star className="h-4 w-4" />
            Reviews
          </TabsTrigger>
        </TabsList>

        <TabsContent value="goals">
          <Card>
            <CardHeader>
              <CardTitle>Goals & Objectives</CardTitle>
              <CardDescription>Track progress towards your goals</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-24 bg-muted rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : goals.length === 0 ? (
                <div className="text-center py-12">
                  <Target className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                  <p className="text-muted-foreground">No goals set yet</p>
                  <Button className="mt-4 gap-2" onClick={() => setShowGoalModal(true)}>
                    <Plus className="h-4 w-4" />
                    Create Your First Goal
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {goals.map(goal => (
                    <div key={goal.id} className="p-4 border rounded-lg hover:border-[#1a365d]/50 transition-colors">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{goal.title}</h3>
                            <Badge className={GOAL_STATUS_COLORS[goal.status]}>{goal.status.replace('_', ' ')}</Badge>
                            <Badge className={PRIORITY_COLORS[goal.priority]}>{goal.priority}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{goal.description}</p>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              Target: {new Date(goal.target_date).toLocaleDateString('en-IN')}
                            </span>
                            <span className="capitalize">{goal.category}</span>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span>Progress</span>
                              <span className="font-semibold">{goal.progress}%</span>
                            </div>
                            <Progress value={goal.progress} className="h-2" />
                          </div>
                        </div>
                        {goal.status !== 'completed' && goal.status !== 'cancelled' && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateGoalProgress(goal.id, Math.min(goal.progress + 25, 100))}
                            >
                              +25%
                            </Button>
                            <Button
                              size="sm"
                              className="btn-primary"
                              onClick={() => handleUpdateGoalProgress(goal.id, 100)}
                            >
                              Complete
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reviews">
          <Card>
            <CardHeader>
              <CardTitle>Performance Reviews</CardTitle>
              <CardDescription>View and manage performance evaluations</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="responsive-table-container">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Employee</TableHead>
                      <TableHead>Review Period</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Rating</TableHead>
                      <TableHead>Goals Achieved</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reviews.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12">
                          <Star className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                          <p className="text-muted-foreground">No performance reviews yet</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      reviews.map(review => (
                        <TableRow key={review.id}>
                          <TableCell className="font-medium">{review.employee_name}</TableCell>
                          <TableCell>{review.review_period}</TableCell>
                          <TableCell className="capitalize">{review.review_type}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {renderStars(Math.round(review.rating))}
                              <span className="ml-2 font-semibold">{review.rating}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-semibold">{review.goals_achieved}</span>
                            <span className="text-muted-foreground">/{review.goals_total}</span>
                          </TableCell>
                          <TableCell>
                            <Badge variant={review.status === 'acknowledged' ? 'default' : 'secondary'}>
                              {review.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => setSelectedReview(review)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Goal Modal */}
      <Dialog open={showGoalModal} onOpenChange={setShowGoalModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-[#1a365d]" />
              Set New Goal
            </DialogTitle>
            <DialogDescription>Define a new objective to track</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateGoal} className="space-y-4">
            <div className="space-y-2">
              <Label>Goal Title *</Label>
              <Input
                required
                value={goalForm.title}
                onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })}
                placeholder="e.g., Complete AWS Certification"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                rows={2}
                value={goalForm.description}
                onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                placeholder="Describe what you want to achieve..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={goalForm.category}
                  onValueChange={(v) => setGoalForm({ ...goalForm, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="performance">Performance</SelectItem>
                    <SelectItem value="development">Development</SelectItem>
                    <SelectItem value="project">Project</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={goalForm.priority}
                  onValueChange={(v) => setGoalForm({ ...goalForm, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Target Date *</Label>
              <Input
                type="date"
                required
                value={goalForm.target_date}
                onChange={(e) => setGoalForm({ ...goalForm, target_date: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowGoalModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Create Goal
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Review Modal */}
      <Dialog open={!!selectedReview} onOpenChange={() => setSelectedReview(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Performance Review Details</DialogTitle>
            <DialogDescription>{selectedReview?.review_period}</DialogDescription>
          </DialogHeader>
          {selectedReview && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <p className="text-sm text-muted-foreground">Employee</p>
                <p className="font-semibold">{selectedReview.employee_name}</p>
              </div>
              <div className="flex items-center justify-center gap-2 py-4">
                {renderStars(Math.round(selectedReview.rating))}
                <span className="text-2xl font-bold ml-2">{selectedReview.rating}/5</span>
              </div>
              {selectedReview.strengths?.length > 0 && (
                <div>
                  <p className="font-semibold text-green-600 mb-2">Strengths</p>
                  <ul className="list-disc list-inside space-y-1">
                    {selectedReview.strengths.map((s, i) => (
                      <li key={i} className="text-sm">{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {selectedReview.improvements?.length > 0 && (
                <div>
                  <p className="font-semibold text-amber-600 mb-2">Areas for Improvement</p>
                  <ul className="list-disc list-inside space-y-1">
                    {selectedReview.improvements.map((s, i) => (
                      <li key={i} className="text-sm">{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {selectedReview.comments && (
                <div>
                  <p className="font-semibold mb-2">Comments</p>
                  <p className="text-sm text-muted-foreground">{selectedReview.comments}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedReview(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
